import { getLocale } from '../../../common/i18n';
import type { ProductOfferingCharacteristic } from '../../../generated/api/productOfferingCharacteristic.js';
import type { ProductOfferingCharacteristicValue } from '../../../generated/api/productOfferingCharacteristicValue.js';

/**
 * Helpers for reading and writing Core Commerce runtime characteristics.
 *
 * Both the catalog definition and the customer's selection use the catalog's own
 * `ProductOfferingCharacteristic` structure, so the basket never carries UI-only fields.
 * Display labels are always resolved here from `localizedValue` instead of being persisted.
 */

/** Selectable options of a catalog characteristic, labelled for the active locale. */
export const getCharacteristicOptions = (
  characteristics: ProductOfferingCharacteristic[] | undefined,
  characteristicName: string
): Array<{ value: string; label: string }> => {
  const characteristic = characteristics?.find(c => c.name === characteristicName);
  const locale = getLocale();
  const language = locale.split('-')[0];
  return (characteristic?.value ?? []).flatMap(characteristicValue => {
    if (!characteristicValue.value) {
      return [];
    }
    const localizedValues = characteristicValue.localizedValue ?? [];
    const label =
      localizedValues.find(lv => lv.locale === locale)?.value ??
      localizedValues.find(lv => lv.locale?.split('-')[0] === language)?.value ??
      // Core Commerce often localizes characteristic values only in en-US, so any English
      // translation beats falling back to the raw technical value (e.g. "single_mode").
      localizedValues.find(lv => lv.locale?.split('-')[0] === 'en')?.value ??
      characteristicValue.value;
    return [{ value: characteristicValue.value, label }];
  });
};

export const getCharacteristicValueLabel = (
  characteristics: ProductOfferingCharacteristic[] | undefined,
  characteristicName: string,
  value: string
): string => getCharacteristicOptions(characteristics, characteristicName).find(o => o.value === value)?.label ?? value;

export const isDefaultCharacteristicValue = (
  characteristicValue: Pick<ProductOfferingCharacteristicValue, '_default'>
): boolean => characteristicValue._default === true || (characteristicValue as { default?: boolean }).default === true;

/** Value the catalog marks as pre-selected. */
export const getDefaultCharacteristicValue = (
  characteristics: ProductOfferingCharacteristic[] | undefined,
  characteristicName: string
): string | undefined =>
  characteristics?.find(c => c.name === characteristicName)?.value?.find(isDefaultCharacteristicValue)?.value;

/** Value the customer selected, stored in the basket as a single entry in `value`. */
export const getSelectedCharacteristicValue = (
  characteristics: ProductOfferingCharacteristic[] | undefined,
  characteristicName: string
): string | undefined => characteristics?.find(c => c.name === characteristicName)?.value?.[0]?.value;

/** Basket representation of a selection. Returns undefined when the catalog does not offer the value. */
export const buildCharacteristicSelection = (
  characteristics: ProductOfferingCharacteristic[] | undefined,
  characteristicName: string,
  selectedValue: string | undefined
): ProductOfferingCharacteristic[] | undefined => {
  const characteristic = characteristics?.find(c => c.name === characteristicName);
  if (!characteristic || !selectedValue || !characteristic.value?.some(v => v.value === selectedValue)) {
    return undefined;
  }
  return [{ name: characteristic.name, type: characteristic.type ?? 'String', value: [{ value: selectedValue }] }];
};
